import {
  selectIsCampaignPerformanceAlertsFeatureFlagOn,
  selectIsCampaignsFavoritesFeatureFlagOn,
  selectIsCampaignsHideFoldersFeatureFlagOn,
  selectIsEmbeddedMessagingCoreFeatureFlagOn,
} from 'core/userContext';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { BUTTON_SIZES, GhostButton, SecondaryButton } from 'ui/button';
import { Favorite16, FavoriteFilled16, FolderClosed, FolderAdd16 } from 'ui/icons';
import { useDebouncedEffect } from 'utils/hooks/useDebouncedEffect';
import { useWhatsAppMessageChannelFeature } from 'utils/hooks/featureFlags';

import {
  LeftMeta,
  RightMeta,
  StyledMeta,
  StyledSpan,
  WideInputText,
} from '../CampaignIndexPage.styles';
import { CampaignIndexFilterMenu } from '../components/CampaignIndexFilterMenu';
import {
  CAMPAIGN_STATE_FILTERS,
  CampaignFilterPrefix,
  FAVORITE_STATE_FILTER,
  SEARCH_TEXT_INPUT_DEBOUNCE_TIMEOUT_MS,
} from '../constants';
import { useCampaignsTranslation } from '../hooks';
import { useCampaignFolderModals } from '../hooks/useCampaignFolderModals';
import { useFlattenedFilters } from '../hooks/useFlattenedFilters';
import { useHasFilters } from '../hooks/useHasFilters';
import { getPrefixToSelectedFiltersMap } from '../utils';
import { useCampaignFiltersContext } from './contexts/CampaignFiltersContext';
import { useSelectionContext } from './contexts/SelectionContext';
import { usePaginationContext } from 'utils/contexts/pagination';
import { colors } from 'ui/theme';
import { IndexTabsList, IndexTabTrigger, TabsGroup } from 'ui/tabsGroup';
import { CampaignFolderMenu } from './CampaignFolderMenu';
import { AllowedActionMenuElements } from 'ui/actionMenu/types';

const displayContent = {
  clear: 'Clear',
  searchPlaceholder: 'Search campaigns',
};

const toString = (value: string | number): string => `${value}`;

export const CampaignTableControls = () => {
  const isCampaignsHideFoldersFeatureFlagOn = useSelector(
    selectIsCampaignsHideFoldersFeatureFlagOn,
  );
  const isEmbeddedMessagingEnabled = useSelector(selectIsEmbeddedMessagingCoreFeatureFlagOn);
  const isCampaignAlertsOn = useSelector(selectIsCampaignPerformanceAlertsFeatureFlagOn);
  const { isWhatsAppEnabled: isWhatsAppOn } = useWhatsAppMessageChannelFeature();
  const isCampaignsFavoritesOn = useSelector(selectIsCampaignsFavoritesFeatureFlagOn);

  const { ct } = useCampaignsTranslation();
  const {
    clearFilters,
    setCampaignTypes,
    setChannelTypes,
    setCreators,
    setLabels,
    setLists,
    setMedia,
    setModifiers,
    searchQuery,
    setSearchQuery,
    stateCategory,
    setStateCategory,
    states,
    setStates,
    setPlacements,
    setAlerts,
    setTemplateType,
    setTemplateStatus,
    setTemplateQualityRating,
    isFavorites,
    setIsFavorites,
    setHideFolders,
    hideFolders,
  } = useCampaignFiltersContext();
  const { clearAll } = useSelectionContext();
  const { setCurrentPage } = usePaginationContext();

  const modals = useCampaignFolderModals();
  const flatSelectedFilters = useFlattenedFilters();
  const canClearFilters = useHasFilters();

  // Saved tab that filters on state categories
  const [searchInputValue, setSearchInputValue] = useState(searchQuery);

  // On debounced changes to `searchInputValue`, save the search to local storage and trigger a GraphQL refetch
  const resetSearchDebouncer = useDebouncedEffect(
    () => {
      setCurrentPage(1);
      setSearchQuery(searchInputValue);
    },
    [searchInputValue],
    {
      skipEffectOnMount: true,
      debounceTimeout: SEARCH_TEXT_INPUT_DEBOUNCE_TIMEOUT_MS,
    },
  );

  const handleClearFilters = () => {
    setCurrentPage(1);
    clearFilters();
    resetSearchDebouncer();
    setStateCategory(CAMPAIGN_STATE_FILTERS[0].id);
  };

  const handleFilterChange = (filters: string[][]) => {
    setCurrentPage(1);
    const selectedFilters = getPrefixToSelectedFiltersMap(
      filters,
      isEmbeddedMessagingEnabled,
      isCampaignAlertsOn,
      isWhatsAppOn,
    );

    if (
      stateCategory !== CAMPAIGN_STATE_FILTERS[0].id &&
      selectedFilters[CampaignFilterPrefix.Status] !== states
    ) {
      setStateCategory(CAMPAIGN_STATE_FILTERS[0].id);
    }

    setLabels((selectedFilters[CampaignFilterPrefix.Label] as number[]) || []);
    setMedia(selectedFilters[CampaignFilterPrefix.Medium]?.map(toString) || []);
    setCampaignTypes(selectedFilters[CampaignFilterPrefix.CampaignType]?.map(toString) || []);
    setChannelTypes(selectedFilters[CampaignFilterPrefix.ChannelType]?.map(toString) || []);
    setLists((selectedFilters[CampaignFilterPrefix.AudienceList] as number[]) || []);
    setStates(selectedFilters[CampaignFilterPrefix.Status]?.map(toString) || []);
    setAlerts(selectedFilters[CampaignFilterPrefix.Alerts]?.map(toString) || []);
    setCreators(selectedFilters[CampaignFilterPrefix.Creator]?.map(toString) || []);
    setModifiers(selectedFilters[CampaignFilterPrefix.ModifiedBy]?.map(toString) || []);
    setTemplateType(selectedFilters[CampaignFilterPrefix.TemplateType]?.map(toString) || []);
    setTemplateStatus(selectedFilters[CampaignFilterPrefix.TemplateStatus]?.map(toString) || []);
    setTemplateQualityRating(
      selectedFilters[CampaignFilterPrefix.TemplateQualityRating]?.map(toString) || [],
    );
    setPlacements((selectedFilters[CampaignFilterPrefix.Placements] as number[]) || []);
  };

  const handleNewFolder = () => {
    clearAll();
    modals.create.toggle(true);
  };

  return (
    <StyledMeta data-test="campaign-table-controls">
      <LeftMeta data-test="campaign-state-filters">
        <TabsGroup
          data-test="campaigns-tabs-filter"
          value={isFavorites ? FAVORITE_STATE_FILTER.id : stateCategory}
        >
          <IndexTabsList>
            {CAMPAIGN_STATE_FILTERS.map(({ id, label, filters }) => (
              <IndexTabTrigger
                key={id}
                value={id}
                onClick={() => {
                  setCurrentPage(1);
                  setIsFavorites(false);
                  setStateCategory(id);
                  setStates(filters);
                }}
              >
                {label}
              </IndexTabTrigger>
            ))}
            {isCampaignsFavoritesOn && (
              <IndexTabTrigger
                key={FAVORITE_STATE_FILTER.id}
                value={FAVORITE_STATE_FILTER.id}
                onClick={() => {
                  setCurrentPage(1);
                  setIsFavorites(true);
                  setStateCategory(CAMPAIGN_STATE_FILTERS[0].id);
                  setStates(CAMPAIGN_STATE_FILTERS[0].filters);
                }}
              >
                <StyledSpan>
                  {isFavorites ? <FavoriteFilled16 color={colors.white} /> : <Favorite16 />}
                  {FAVORITE_STATE_FILTER.label}
                </StyledSpan>
              </IndexTabTrigger>
            )}
          </IndexTabsList>
        </TabsGroup>
      </LeftMeta>
      <RightMeta>
        {isCampaignsHideFoldersFeatureFlagOn ? (
          <CampaignFolderMenu
            hideFolders={hideFolders}
            onClick={() => {
              setCurrentPage(1);
              setHideFolders(!hideFolders);
            }}
            options={[
              {
                id: 'create-folder',
                as: AllowedActionMenuElements.Button,
                content: (
                  <>
                    <FolderAdd16 /> <span>New folder</span>
                  </>
                ),
                onClick: handleNewFolder,
              },
            ]}
          />
        ) : (
          <SecondaryButton size={BUTTON_SIZES.LARGE} onClick={handleNewFolder}>
            <FolderClosed />
            <span>{ct('button.new_folder')}</span>
          </SecondaryButton>
        )}

        {canClearFilters && (
          <GhostButton
            data-test="clear-campaign-filters-button"
            onClick={handleClearFilters}
            tabIndex={0}
            size={BUTTON_SIZES.LARGE}
          >
            {displayContent.clear}
          </GhostButton>
        )}
        <CampaignIndexFilterMenu
          onSelect={handleFilterChange}
          selectedFilters={flatSelectedFilters}
        />
        <WideInputText
          data-test="campaign-search-input"
          id="campaign-search-input"
          onChange={setSearchInputValue}
          placeholder={displayContent.searchPlaceholder}
          value={searchInputValue}
        />
      </RightMeta>
    </StyledMeta>
  );
};
